import React, { useState, useRef, useEffect, useCallback } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom'
import usePrototypes from '../hooks/usePrototypes.js';
import PrototypeCard from './PrototypeCard.js';
import { Link, useParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { trackEvent } from '../services/analytics.js';

const ExploreSection = React.memo(function ExploreSection({
  username, jumpList, hasMore, fetchMoreJump, exploreList, inputRef, exploreTitleRef
}) {
  const isLoading = exploreList.length === 0;

  if (isLoading) {
    // you can return null (nothing), or a spinner/placeholder
    return null;
  }

  const hasPrototypes = jumpList.length > 0;

  return (
    <div className="explore-section" style={{padding: 20 }}>
     <div className="innerExplore">
        {/* Jump Back In (static top 5) */}
          <div className="jumpBack">
          <h2 style={{ margin: 0, marginBottom: 16}}>
            {hasPrototypes ? 'Jump Back In' : 'Jump Right In'}
          </h2>
          <div className="jumpStyles">
            {hasPrototypes ? (
              jumpList.slice(0, 3).map((p, i) => (
                <div
                  key={p.chatId ? `${p.chatId}-${i}` : i}
                  className="zoom-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                  onClick={() => {
                    trackEvent({
                      action:   'click_jump_back_in',
                      category: 'navigation',
                      label:    p.chatId || `item-${i}`
                    });
                  }}
                >
                  <PrototypeCard proto={p} />
                </div>
              ))
            ) : null}

            {/* Create New placeholder */}
            <div
              className="zoom-card"
              style={{
                animationDelay: `${jumpList.slice(0,5).length * 0.3}s`,
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                border: '2px dashed #ccc',
                borderRadius: 6,
                overflow: 'hidden',
                cursor: 'pointer',
              }}
               onClick={() => {
                trackEvent({
                  action:   'click_jump_back_in',
                  category: 'navigation',
                  label:    'create_new'
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => {
                  inputRef.current?.focus();
                }, 400);
              }}
            >
              <div
                style={{
                  height: 200,
                  background: '#f3f3f3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: 48, color: '#bbb' }}>＋</span>
              </div>
              <div style={{ padding: '30px 0', textAlign: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 15 }}>
                  Create New
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Explore (infinite scroll) */}
        <div className="exploreContainer">
          <h2 ref={exploreTitleRef} style={{ marginBottom: '16', textAlign: 'left', marginTop: '0'   }}>Explore</h2>
          <InfiniteScroll
            dataLength={exploreList.length}
            next={fetchMoreJump}
            hasMore={hasMore}
            loader={<div style={{ textAlign: 'center', padding: 16 }}>Loading…</div>}
          >
        <div className="exploreGrid"
        >
                {exploreList.map((p, i) => (
                  <div
                    key={p.chatId ? `${p.chatId}-${i}` : i}
                    className="zoom-card"
                    style={{ animationDelay: `${i * 0.1}s` }}
                    onClick={() => {
                      trackEvent({
                        action:   'click_explore_item',
                        category: 'navigation',
                        label:    p.chatId || `item-${i}`
                      });
                    }}
                  >
                    <PrototypeCard proto={p} />
                  </div>
                ))}
        </div>

          </InfiniteScroll>
        </div>
      </div>
    </div>
  );
});


export default function Onboarding({ onStart }) {
  const { username } = useParams();
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [selectedTag, setSelectedTag] = useState('Open');
  const [hoveredTag, setHoveredTag] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const exploreTitleRef = useRef(null);
  const isFirstLoad = useRef(true);

  // ── pull in published prototypes for Explore with pagination ──
  const PAGE_SIZE = 20;
  const [publishedList, setPublishedList] = useState([]);
  const [publishedOffset, setPublishedOffset] = useState(0);
  const [publishedHasMore, setPublishedHasMore] = useState(true);
  const [publishedLoading, setPublishedLoading] = useState(true);

  const fetchMorePublished = React.useCallback(() => {
    fetch(`/api/published?offset=${publishedOffset}`)
      .then(r => r.json())
      .then(data => {
        setPublishedList(prev => [...prev, ...data]);
        setPublishedOffset(prev => prev + PAGE_SIZE);
        setPublishedHasMore(data.length === PAGE_SIZE);
        setPublishedLoading(false);
      })
      .catch(console.error);
  }, [publishedOffset]);

  // Initial load
  useEffect(() => {
    setPublishedList([]);
    setPublishedOffset(0);
    setPublishedHasMore(true);
    setPublishedLoading(true);
    fetch(`/api/published?limit=${PAGE_SIZE}&offset=0`)
      .then(r => r.json())
      .then(data => {
        setPublishedList(data);
        setPublishedOffset(PAGE_SIZE);
        setPublishedHasMore(data.length === PAGE_SIZE);
        setPublishedLoading(false);
      })
      .catch(console.error);
  }, []);

  // ── pull in user's own prototypes for Jump Back In ──
  const { data: myPrototypes = [], loading: myLoading } = usePrototypes();

  // ── infinite‐scroll state for Jump Back In (user's own prototypes) ──
  const JUMP_PAGE_SIZE = 10;
  const [jumpList, setJumpList] = useState(myPrototypes.slice(0, JUMP_PAGE_SIZE));
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(myPrototypes.length > JUMP_PAGE_SIZE);

  useEffect(() => {
    setJumpList(myPrototypes.slice(0, JUMP_PAGE_SIZE));
    setPage(1);
    setHasMore(myPrototypes.length > JUMP_PAGE_SIZE);
  }, [myPrototypes]);

  const fetchMoreJump = React.useCallback(() => {
    const nextPage = page + 1;
    const start = page * JUMP_PAGE_SIZE;
    const more = myPrototypes.slice(start, start + JUMP_PAGE_SIZE);

    setJumpList(prev => [...prev, ...more]);
    setPage(nextPage);
    if (more.length < JUMP_PAGE_SIZE) setHasMore(false);
  }, [page, myPrototypes]);

  // Submission handler
  const handleSubmit = (e, overrideText) => {
    e?.preventDefault();
    const submission = overrideText ?? text;
    if (!submission.trim()) return setError('Please provide a description or instruction');
    setError('');
    trackEvent({
      action:   'submit_prompt',
      category: 'engagement',
      label:    submission
    });
    onStart(submission, file);
    inputRef.current?.focus();
  };

  // Tag selection
  const tags = ['Open', 'for product teams', 'for kids'];
  const handleTagClick = (tag) => setSelectedTag(tag);
  const handleTagHover = (tag) => setHoveredTag(tag);



  // Styles
  const bubbleBase = {
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background 0.2s, opacity 0.2s, transform 0.2s'
  };
  const getBgGradient = () => {
    if (selectedTag === 'for kids')
      return 'linear-gradient(to right top, #d16ba5, #c777b9, #ba83ca, #aa8fd8, #9a9ae1, #8aa7ec, #79b3f4, #69bff8, #52cffe, #41dfff, #46eefa, #5ffbf1)';
    if (selectedTag === 'for product teams')
      return 'linear-gradient(to right, #17CAA1, #2F79FF)';
    return 'none';
  };

  const isKids = selectedTag === 'for kids';
  const isProduct = selectedTag === 'for product teams';
  const handleTextChange = (e) => {
    setError('');
    setText(e.target.value);
    const ta = inputRef.current;
    if (ta) {
      ta.style.height = 'auto';
      if (ta.scrollHeight > ta.clientHeight) ta.style.height = `${ta.scrollHeight}px`;
    }
  };

  // focus textarea on mount & whenever the tab changes
useEffect(() => {
  inputRef.current?.focus();
}, [selectedTag]);

// Prompt definitions
  const kidsPrompts = [
  { text: 'Build me a game to catch falling emojis 🎮👇', value: 'checkers' },
  { text: 'Create crazy animated vector art', value: 'animation' },
  { text: 'Design a drawing pad 🖌️📝', value: 'drawingpad' },
  { text: 'Build a math quiz that gets harder as you go ➗➕❓', value: 'mathquiz' },
  { text: 'Make a silly face generator 🤪🙂', value: 'facegen' },
  { text: 'Make a checkers game 🎮👇', value: 'facegen' },
  { text: 'Make a color palette generator 🎨🖌️', value: 'palettegen' },
  { text: 'Make a 3 letter kids NYT wordle game 📝', value: 'palettegen' },
  { text: 'Create an interactive story 📖✨', value: 'interactivestory' },
  { text: 'Build a drawing canvas 🖼️✏️', value: 'drawingcanvas' },
  { text: 'Create a virtual pet 🐶👾', value: 'virtualpet' },
  { text: 'Paint a rainbow scene 🌈🖌️', value: 'rainbow' },
  { text: 'Create a sound visualizer', value: 'sound' },
];

  const productPrompts = [
    { text: 'Prototype a modern search experience', value: 'search' },
    { text: 'Create a sleek landing page', value: 'landing' },
    { text: 'Generate a table UI', value: 'tableui' },
    { text: 'Build a feedback form', value: 'feedback' },
    { text: 'Design a weather app', value: 'weatherapp' },
    { text: 'Create a to-do list', value: 'todolist' },
    { text: 'Build a chatbot interface', value: 'chatbot' },
    { text: 'Generate a stylish color palette generator', value: 'palette' },
    { text: 'Create a user profile form', value: 'profileform' },
    { text: 'Design an admin panel', value: 'adminpanel' }
  ];

  const getPrompts = () => {
    if (isKids) return kidsPrompts;
    if (isProduct) return productPrompts;
    return [...kidsPrompts, ...productPrompts];
  };

  // Generate prompt items once per tab
  const [promptItems, setPromptItems] = useState([]);
  // Color palettes based on selected category
const colorPalettes = {
  kids: ['#ff5f7e', '#ffbe0b', '#fff', '#1982c4', '#6a4c93'],
  product: ['#fff', '#fff', '#eee', '#eee', '#ddd'],
  open: [
    '#e63946', // scarlet
    '#f4a261', // tangerine
    '#e9c46a', // lemon
    '#2a9d8f', // teal
    '#264653', // deep slate
    '#8d99ae', // cool gray
    '#8338ec', // electric purple
    '#06d6a0', // mint
    '#ffbe0b', // sunshine
    '#ff006e', // hot pink
  ]
};


const getRandomColor = () => {
  const key = isKids ? 'kids' : isProduct ? 'product' : 'open';
  const palette = colorPalettes[key];
  return palette[Math.floor(Math.random() * palette.length)];
};

const [promptBand, setPromptBand] = useState({ min: 0, max: 0 });

// Measure the available Y band for prompts
const measurePromptBand = useCallback(() => {
  if (!inputRef.current || !exploreTitleRef.current) return;
  const inputRect = inputRef.current.getBoundingClientRect();
  const exploreRect = exploreTitleRef.current.getBoundingClientRect();
  // Use window scrollY to get absolute Y
  const min = inputRect.bottom + window.scrollY - 50;
  const max = exploreRect.top + window.scrollY - 80;
  setPromptBand({ min, max });
}, []);

useEffect(() => {
  measurePromptBand();
  window.addEventListener('resize', measurePromptBand);
  return () => window.removeEventListener('resize', measurePromptBand);
}, [measurePromptBand]);

useEffect(() => {
  const allPrompts = getPrompts();
  const displayCount = isKids || isProduct ? 5 : 5;
  const selection = allPrompts
    .sort(() => Math.random() - 0.5)
    .slice(0, displayCount);
  const items = selection.map((prompt, i) => {
    const size = Math.floor(Math.random() * 20) + 14;
    // If band is valid, randomize Y within it, else fallback to 120-160px
    let top = 120 + Math.random() * 40;
    if (promptBand.max - promptBand.min > 40) {
      top = promptBand.min + Math.random() * (promptBand.max - promptBand.min - size);
    }
    const duration = 18 + Math.random() * 6;
    const delay = -Math.random() * duration;
    return {
      id: `${prompt.value}-${i}`,
      text: prompt.text,
      style: {
        fontWeight: `100`,
        fontFamily: `courier new`,
        fontSize: `${size}px`,
        top: `${top}px`,
        position: 'absolute',
        right: '-200px',
        animation: `flyLeft ${duration}s linear infinite`,
        animationDelay: `${delay}s`,
        color: getRandomColor(),
      }
    };
  });
  setPromptItems(items);
}, [selectedTag, promptBand]);

  const [publishedThumbs, setPublishedThumbs] = useState([])

  const navigate = useNavigate()
  useEffect(() => {
    fetch('/api/published')
      .then(r => r.json())
      .then(data => {
        const items = data.map((row, i) => {
          const size     = 60 + Math.random() * 60
          const bottom   = 5  + Math.random() * 60
          const duration = 12 + Math.random() * 8
          const delay    = -Math.random() * duration
          return {
            id:          `${row.chat_id}-${row.version_id}-${i}`,
            url:         row.thumbnailUrl,
            chat_id:     row.chat_id,
            version_id:  row.version_id,
            username:    row.username,
            style: {
              position:       'absolute',
              width:          `${size}px`,
              bottom:         `${bottom}%`,
              right:          `-${size}px`,
              animation:      `flyLeft ${duration}s linear infinite`,
              animationDelay: `${delay}s`,
              pointerEvents:  'auto',    // allow clicks
            }
          }
        })
        setPublishedThumbs(items)
      })
      .catch(console.error)
  }, [])

  // Just use the publishedList directly for Explore
  const exploreList = publishedList;

  useEffect(() => {
    // Use requestAnimationFrame for next paint
    const id = requestAnimationFrame(measurePromptBand);
    return () => cancelAnimationFrame(id);
  }, [measurePromptBand, publishedList]);

  return (
    <div
      className="animated-bg"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '16px',
        position: 'relative',
        overflow: 'visible',
        backgroundImage: getBgGradient()
      }}
    >
      {/* Floating Shapes */}
      <div className="floating-shapes">
        {[...Array(10)].map((_, i) => {
          const size = Math.floor(Math.random() * 140) + 10;
          const left = Math.random() * 100;
          const duration = Math.floor(Math.random() * 20) + 3;
          const shapes = ['circle', 'square', 'triangle', 'hexagon', 'diamond'];
          const shape = shapes[Math.floor(Math.random() * shapes.length)];
          const colors = ['#ff99cc', '#66ccff', '#a0e7e5', '#bdb2ff', '#ffd6a5', '#ffadad', '#caffbf', '#9bf6ff', '#ffc6ff', '#fdffb6'];
          const color = colors[Math.floor(Math.random() * colors.length)];
          return (
            <div
              key={i}
              className={`shape ${shape}`}
              style={{ left: `${left}%`, position: 'fixed', animationDuration: `${duration}s`, '--shape-size': `${size}px`, '--shape-color': color, }}
            />
          );
        })}
      </div>

      {/* Header */}
      <div className="header-container">
        <h1 className={`header-title ${isKids ? 'kids-gradient-text' : ''}`}>Unleash your Creativity.</h1>
        <h2 className={`header-subtitle ${isKids ? 'kids-gradient-text' : ''}`}>Prototype. Experiment. Explore.</h2>
      </div>

      {/* Tag bubbles */}
      <div className="tag-bubbles">
        {tags.map(tag => {
          const isSelected = tag === selectedTag;
          const isHovered = tag === hoveredTag;
          return (
            <span
              key={tag}
              onClick={() => handleTagClick(tag)}
              onMouseEnter={() => handleTagHover(tag)}
              onMouseLeave={() => handleTagHover(null)}
              style={{
                ...bubbleBase,
                background: isSelected ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.15)',
                border: isSelected ? '1px solid rgba(0,0,0,0.4)' : '1px solid rgba(0,0,0,0.2)',
                backdropFilter: 'blur(10px)', color:'#333', opacity: isSelected ? 1 : isHovered ? 0.85 : 0.7,
                fontWeight: isSelected ? 600 : 400, transform: isHovered && !isSelected ? 'scale(1.05)' : 'scale(1)',
              }}
            >
              {tag}
            </span>
          );
        })}
      </div>

      {/* Input area */}
      <div className="input-group">
        <form onSubmit={handleSubmit} className="onboarding-form">
          <textarea
            ref={inputRef}
            placeholder="Start here: What should we build?"
            value={text}
            onChange={handleTextChange}
            rows={1}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
            onPaste={e => {
              // Only allow plain text pastes
              e.preventDefault();
              const plain = e.clipboardData.getData('text/plain');
              if (!plain) return;
              const ta = inputRef.current;
              const start = ta.selectionStart;
              const end = ta.selectionEnd;
              const newText = text.slice(0, start) + plain + text.slice(end);
              setText(newText);
              // reposition cursor
              const pos = start + plain.length;
              requestAnimationFrame(() => ta.setSelectionRange(pos, pos));
            }}
            onDrop={e => {
              // Block dropping files into textarea
              if (e.dataTransfer.files.length > 0) {
                e.preventDefault();
              }
            }}
            className="resizable-input"
          />
          <button type="submit" className="magic-button2"><svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#FFFFFF"><path d="M647-440H160v-80h487L423-744l57-56 320 320-320 320-57-56 224-224Z"/></svg></button>
        </form>
      </div>

      {/* Floating Prompts */}
      {promptBand.max - promptBand.min > 40 && (
        <div className="floating-prompts" style={{ position: 'absolute', left: 0, top: 0, width: '100%', pointerEvents: 'none', zIndex: 30 }}>
          {promptItems.map(({ id, text: promptText, style }) => (
            <div
              key={id}
              className="prompt"
              style={{
                ...style,
                cursor: 'pointer',
                pointerEvents: 'auto',
                ...(isKids && {
                  textShadow: `1px 1px 2px #333`
                })
              }}
              onMouseEnter={e => {
                e.currentTarget.style.animationPlayState = 'paused';
                e.currentTarget.style.fontWeight = 'bold';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.animationPlayState = 'running';
                e.currentTarget.style.fontWeight = style.fontWeight || 'normal';
              }}
                   onClick={() => {
                     trackEvent({
                       action:   'click_floating_prompt',
                       category: 'interaction',
                       label:    promptText
                     });
                     handleSubmit(null, promptText);
                   }}
            >
              {promptText}
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && <div style={{ color:'#d32f2f', fontSize:'14px', marginTop:'16px', textAlign:'center' }}>{error}</div>}

      {/* ─── EXPLORE SECTION ─── */}
      <div style={{ position: 'relative', zIndex: 20, width: '100%' }}>
        <ExploreSection
          jumpList={jumpList}
          username={username}
          hasMore={publishedHasMore}
          fetchMoreJump={fetchMorePublished}
          exploreList={exploreList}
          inputRef={inputRef}
          exploreTitleRef={exploreTitleRef}
        />
      </div>
    </div>
  );
}
